import {useGrpcRequest} from '@aboutyou/data-fetcher';
import {Session} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/session.types';
import {BasketPageService_GetBasketPageBulkLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.grpc.lazy';
import {GetBasketPageBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.types';
import {useFullSession} from '@aboutyou/user';
import {useReductionsStateRequestParameter} from '../../../features/ReductionsState/useReductionsStateRequestParameter';
import {useTargetGroupIdentifier} from '../../../shared-deprecated/useTargetGroupIdentifier';

interface BasketPageConfig {
  disabled?: boolean;
  onSuccess?: (data: GetBasketPageBulkResponse) => void;
}

const useBasketPageSection = <Section extends keyof GetBasketPageBulkResponse>(
  sectionName: Section,
  config?: BasketPageConfig,
) => {
  const session = useFullSession();
  const targetGroupIdentifier = useTargetGroupIdentifier();

  return useGrpcRequest(
    BasketPageService_GetBasketPageBulkLazy,
    {
      session: session as Session,
      targetGroup: targetGroupIdentifier,
      reductionsState: useReductionsStateRequestParameter(),
    },
    {
      section: sectionName,
      disabled: !session || config?.disabled,
      // Prevents from showing stale data on basket page, eg user adds new item to the basket
      // without this he won't see the updated price and deliveries.
      //
      // This causes a BasketPageService_GetBasketPageBulk request everytime the user visits
      // the page.
      staleTime: 0,
      onSuccess: config?.onSuccess,
    },
  );
};

export const useBasketAdvertisement = (config?: BasketPageConfig) => {
  return useBasketPageSection('advertisementSection', config);
};

export const useBasketInfoSection = (config?: BasketPageConfig) => {
  return useBasketPageSection('informationSection', config);
};

export const useBasketPageUSPs = (config?: BasketPageConfig) => {
  return useBasketPageSection('uspsSection', config);
};

export const useUnavailableProducts = (config?: BasketPageConfig) => {
  return useBasketPageSection('unavailableProducts', config);
};

export const useBasketTotalsSection = (config?: BasketPageConfig) => {
  return useBasketPageSection('totalPriceSection', config);
};

export const useDeliveriesSection = (config?: BasketPageConfig) => {
  return useBasketPageSection('deliveriesSection', config);
};

export const useBasketFAQ = (config?: BasketPageConfig) => {
  return useBasketPageSection('faqSection', config);
};

export const useCustomerService = (config?: BasketPageConfig) => {
  return useBasketPageSection('customerService', config);
};

export const useBasketPageTitleSection = (config?: BasketPageConfig) => {
  return useBasketPageSection('titleSection', config);
};

export const useComplementaryRecommendation = (config?: BasketPageConfig) => {
  return useBasketPageSection('complementaryRecommendationSection', config);
};

export const useDiscountCardInformationSection = (
  config?: BasketPageConfig,
) => {
  return useBasketPageSection('discountCardInformationSection', config);
};

export const useBasketMovSection = (config?: BasketPageConfig) => {
  return useBasketPageSection('movSection', config);
};

export const useBasketTrackerSection = () => {
  return useBasketPageSection('basketTracker');
};

export const useBasketHintSection = () => {
  return useBasketPageSection('hintSection');
};

export const useBasketCoinsSection = () => {
  return useBasketPageSection('coinsSection');
};

export const useExpressCheckoutSection = (disabled: boolean) => {
  return useBasketPageSection('expressCheckoutSection', {
    disabled,
  });
};
